import { initEventGateway } from '@streamr/utils';
import { FunctionFragment, } from 'ethers';
import { EventEmitter } from 'eventemitter3';
import without from 'lodash/without';
import pLimit from 'p-limit';
export async function waitForTx(txToSubmit) {
    const tx = await txToSubmit;
    return tx.wait(); // cannot be null unless arg confirmations set to 0
}
const isTransactionResponse = (returnValue) => {
    return (returnValue.wait !== undefined && (typeof returnValue.wait === 'function'));
};
const createLogger = (eventEmitter, loggerFactory) => {
    const logger = loggerFactory.createLogger('contract');
    eventEmitter.on('onMethodExecute', (methodName) => {
        logger.debug('Execute method', { methodName });
    });
    eventEmitter.on('onTransactionSubmit', (methodName, tx) => {
        logger.debug('Submit transaction', {
            method: methodName,
            tx: tx.hash,
            to: tx.to,
            nonce: tx.nonce,
            gasLimit: tx.gasLimit,
            gasPrice: tx.gasPrice
        });
    });
    eventEmitter.on('onTransactionConfirm', (methodName, receipt) => {
        logger.debug('Received transaction confirmation', {
            method: methodName,
            tx: receipt?.hash,
            block: receipt?.blockNumber,
            confirmations: receipt?.confirmations,
            gasUsed: receipt?.gasUsed,
            events: receipt?.logs ?? []
        });
    });
};
const withErrorHandling = async (execute, methodName, action) => {
    try {
        return await execute();
    }
    catch (e) {
        const suffixes = without(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        ['reason', 'code'].map((field) => (e[field] !== undefined ? `${field}=${e[field]}` : undefined)), undefined);
        const wrappedError = new Error(`Error while ${action} contract call "${methodName}"${(suffixes.length > 0) ? ', ' + suffixes.join(', ') : ''}`);
        // @ts-expect-error unknown property
        wrappedError.reason = e;
        throw wrappedError;
    }
};
const createWrappedContractMethod = (contract, contractName, methodName, eventEmitter, concurrencyLimit) => {
    const originalMethod = contract[methodName];
    const methodFullName = `${contractName}.${methodName}`;
    const fn = async (...args) => {
        const returnValue = await withErrorHandling(() => concurrencyLimit(() => {
            eventEmitter.emit('onMethodExecute', methodFullName);
            return originalMethod(...args);
        }), methodFullName, 'executing');
        if (isTransactionResponse(returnValue)) {
            eventEmitter.emit('onTransactionSubmit', methodFullName, returnValue);
            const originalWait = returnValue.wait.bind(returnValue);
            returnValue.wait = async (confirmations, timeout) => {
                const receipt = await withErrorHandling(() => originalWait(confirmations, timeout), methodName, 'waiting transaction for');
                eventEmitter.emit('onTransactionConfirm', methodFullName, receipt);
                return receipt;
            };
        }
        return returnValue;
    };
    // TODO add also other methods in the future if needed:
    // https://docs.ethers.org/v6/api/contract/#BaseContractMethod
    fn.estimateGas = (...args) => originalMethod.estimateGas(...args);
    return fn;
};
/**
 * Adds error handling, logging and limits concurrency.
 *
 * You can use the decorated contract normally, e.g.:
 *     const tx = await contract.createFoobar(123)
 *     return await tx.wait()
 * or
 *     await contract.getFoobar(456)
 */
export const createDecoratedContract = (contract, contractName, loggerFactory, maxConcurrentCalls) => {
    const eventEmitter = new EventEmitter();
    const concurrencyLimit = pLimit(maxConcurrentCalls);
    const decoratedContract = {
        eventEmitter,
        getAddress: () => contract.getAddress(),
        // TODO implement also other generic contract methods in the future if needed
        on: (eventName, listener) => {
            contract.on(eventName, listener);
        },
        off: (eventName, listener) => {
            contract.off(eventName, listener);
        }
    };
    /*
     * Wrap each contract function. We read the list of functions from contract.functions, but
     * actually delegate each method to contract[methodName]. Those methods are almost identical
     * to contract.functions[methodName] methods. The major difference is the way of handling
     * single-value results: the return type of contract.functions[methodName] is always
     * Promise<Result> (see https://docs.ethers.org/v6/api/contract/#BaseContract)
     */
    const methodNames = contract.interface.fragments.filter((f) => FunctionFragment.isFunction(f)).map((f) => f.name);
    methodNames.forEach((methodName) => {
        decoratedContract[methodName] = createWrappedContractMethod(contract, contractName, methodName, eventEmitter, concurrencyLimit);
    });
    createLogger(eventEmitter, loggerFactory);
    return decoratedContract;
};
export const initContractEventGateway = (opts) => {
    const logger = opts.loggerFactory.createLogger('contract');
    initEventGateway(opts.targetName, (emit) => {
        const listener = (eventArgs, blockNumber) => {
            let targetEvent;
            try {
                targetEvent = opts.transformation(eventArgs, blockNumber);
            }
            catch (err) {
                logger.error('Skip emit event', {
                    eventName: opts.targetName,
                    reason: err?.message
                });
                return;
            }
            emit(targetEvent);
        };
        opts.sourceEmitter.on({
            onEvent: listener,
            ...opts.sourceDefinition
        });
        return listener;
    }, (listener) => {
        opts.sourceEmitter.off({
            onEvent: listener,
            ...opts.sourceDefinition
        });
    }, opts.targetEmitter);
};
